import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ArrowLeft, Users, MessageSquare, DollarSign, Clock, CheckCircle, XCircle, Gift, X, Wifi, WifiOff, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { ENLogo } from '@/components/ENLogo';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = `${process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/messages`;

export default function MessagesPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typingUser, setTypingUser] = useState(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectRef = useRef(null);

  const [offerTitle, setOfferTitle] = useState('');
  const [offerDescription, setOfferDescription] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [offerDeliveryDays, setOfferDeliveryDays] = useState('');
  const [sendingOffer, setSendingOffer] = useState(false);

  const isVendor = user?.role === 'vendor' || user?.roles?.includes('vendor');
  const token = localStorage.getItem('token');

  // Kubernetes ingress in the Emergent preview does not support WS upgrade,
  // so after 2 failed attempts we give up on WS and rely on REST polling.
  // This keeps the console clean and avoids an infinite reconnect loop.
  const wsFailuresRef = useRef(0);
  const wsAbandonedRef = useRef(false);
  const WS_MAX_FAILURES = 2;

  // ── WebSocket Connection (best-effort) ──
  const connectWs = useCallback(() => {
    if (!token || wsAbandonedRef.current) return;
    let ws;
    try {
      ws = new WebSocket(`${WS_URL}?token=${token}`);
    } catch {
      wsAbandonedRef.current = true;
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      wsFailuresRef.current = 0;
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    };

    ws.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      switch (data.type) {
        case 'new_message':
          // Add message to current thread if matching
          if (data.sender_id === userId) {
            setMessages(prev => [...prev, { message_id: data.message_id, sender_id: data.sender_id, recipient_id: data.recipient_id, content: data.content, booking_id: data.booking_id, created_at: data.created_at, message_type: 'text' }]);
          }
          // Update conversation list
          fetchConversations();
          break;
        case 'message_sent':
          setMessages(prev => [...prev, { message_id: data.message_id, sender_id: data.sender_id, recipient_id: data.recipient_id, content: data.content, booking_id: data.booking_id, created_at: data.created_at, message_type: 'text' }]);
          break;
        case 'unread_count':
          setUnreadCount(data.count);
          break;
        case 'typing':
          setTypingUser(data.user_id);
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
          break;
        case 'pong':
          break;
        default:
          break;
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      wsFailuresRef.current += 1;
      if (wsFailuresRef.current >= WS_MAX_FAILURES) {
        wsAbandonedRef.current = true;
        return;
      }
      reconnectRef.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => {
      // Silence the browser console — K8s ingress doesn't upgrade to WS here,
      // so errors are expected. Polling fallback takes over.
      try { ws.close(); } catch { /* no-op */ }
    };

    return () => { try { ws.close(); } catch { /* no-op */ } };
  }, [token, userId]);

  useEffect(() => {
    const cleanup = connectWs();
    return () => { cleanup?.(); wsRef.current?.close(); if (reconnectRef.current) clearTimeout(reconnectRef.current); };
  }, [connectWs]);

  // Keep-alive ping
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // ── Data Fetching ──
  useEffect(() => { fetchConversations(); if (userId) fetchThread(userId); }, [userId]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Polling fallback — active whenever WS is not connected.
  // Polls both the open thread and the conversation list so unread badges
  // stay fresh. 4 s cadence to keep server load low.
  useEffect(() => {
    if (wsConnected) return;
    const interval = setInterval(() => {
      if (userId) fetchThread(userId);
      fetchConversations();
    }, 4000);
    return () => clearInterval(interval);
  }, [wsConnected, userId]);

  // Mark messages as read via WS
  useEffect(() => {
    if (userId && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'mark_read', other_user_id: userId }));
    }
  }, [userId, messages.length]);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API}/messages/conversations`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
        if (userId) { const conv = data.find(c => c._id === userId); if (conv) setSelectedUser(conv.other_user); }
      }
    } catch {} finally { setLoading(false); }
  };

  const fetchThread = async (otherId) => {
    try {
      const response = await fetch(`${API}/messages/thread/${otherId}/`, { headers: getAuthHeaders() });
      if (response.ok) setMessages(await response.json());
    } catch {}
  };

  // ── Send Message via WebSocket ──
  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !userId) return;

    // Check for external payment patterns client-side
    const lc = newMessage.toLowerCase();
    const hasExternal = /venmo|cash\s*app|cashapp|zelle|apple\s*pay|google\s*pay|paypal\.me|paypal\.com|bitcoin|crypto|wire\s*transfer/.test(lc);
    const hasLink = /https?:\/\/|www\.|\.com\b|\.net\b|\.org\b/.test(lc);
    const hasPhone = /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/.test(newMessage);

    if (hasExternal || hasLink || hasPhone) {
      toast.warning('We are not liable for unfulfilled transactions outside the platform. EntrepreNatives provides escrow protection to ensure you get your services — you are at risk if you take transactions outside the platform.', { duration: 8000 });
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'send_message', recipient_id: userId, content: newMessage }));
      setNewMessage('');
    } else {
      fetch(`${API}/messages/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ recipient_id: userId, content: newMessage })
      }).then(async (res) => {
        const data = await res.json();
        if (data.warning) toast.warning(data.warning, { duration: 8000 });
        setNewMessage(''); fetchThread(userId);
      }).catch(() => toast.error('Failed to send'));
    }
  };

  // Typing indicator
  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && userId) {
      wsRef.current.send(JSON.stringify({ type: 'typing', recipient_id: userId }));
    }
  };

  const sendCustomOffer = async () => {
    if (!offerTitle || !offerPrice || !offerDeliveryDays || !userId) { toast.error('Fill in all required fields'); return; }
    setSendingOffer(true);
    try {
      const response = await fetch(`${API}/messages/send-offer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ recipient_id: userId, offer_title: offerTitle, offer_description: offerDescription, offer_price: parseFloat(offerPrice), delivery_days: parseInt(offerDeliveryDays) })
      });
      if (response.ok) { toast.success('Custom offer sent!'); setShowOfferModal(false); setOfferTitle(''); setOfferDescription(''); setOfferPrice(''); setOfferDeliveryDays(''); fetchThread(userId); }
      else { const err = await response.json(); throw new Error(err.detail || 'Failed'); }
    } catch (err) { toast.error(err.message); } finally { setSendingOffer(false); }
  };

  const handleOfferAction = async (messageId, action) => {
    try {
      const response = await fetch(`${API}/messages/offer-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ message_id: messageId, action })
      });
      if (response.ok) { const data = await response.json(); toast.success(action === 'accept' ? 'Offer accepted!' : 'Offer declined'); fetchThread(userId); if (action === 'accept' && data.booking_id) navigate(`/booking/custom/${data.booking_id}`); }
      else throw new Error('Failed');
    } catch (err) { toast.error(err.message); }
  };

  const renderOfferMessage = (msg) => {
    const offer = msg.offer_data;
    const isSender = msg.sender_id === user?.user_id;
    return (
      <div className={`max-w-[85%] p-4 rounded-2xl ${isSender ? 'bg-gradient-to-br from-[#8B5CF6]/20 to-[#3B82F6]/20 border border-[#8B5CF6]/30 rounded-br-none' : 'bg-gradient-to-br from-[#8B5CF6]/20 to-[#3B82F6]/20 border border-[#8B5CF6]/30 rounded-bl-none'}`}>
        <div className="flex items-center gap-2 mb-3"><Gift className="w-5 h-5 text-[#A78BFA]" /><span className="text-[#A78BFA] font-bold text-sm">CUSTOM OFFER</span></div>
        <h4 className="text-white font-bold text-lg mb-2">{offer?.title}</h4>
        {offer?.description && <p className="text-[#9CA3AF] text-sm mb-3">{offer.description}</p>}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1 text-green-400"><DollarSign className="w-4 h-4" /><span className="font-mono font-bold">${offer?.price}</span></div>
          <div className="flex items-center gap-1 text-[#9CA3AF]"><Clock className="w-4 h-4" /><span className="text-sm">{offer?.delivery_days} days</span></div>
        </div>
        {offer?.status === 'pending' && !isSender && (
          <div className="flex gap-2 mt-4">
            <Button onClick={() => handleOfferAction(msg.message_id, 'accept')} className="flex-1 bg-green-500 hover:bg-green-400 text-white font-bold" data-testid="accept-offer"><CheckCircle className="w-4 h-4 mr-2" /> Accept</Button>
            <Button onClick={() => handleOfferAction(msg.message_id, 'decline')} variant="outline" className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="decline-offer"><XCircle className="w-4 h-4 mr-2" /> Decline</Button>
          </div>
        )}
        {offer?.status === 'accepted' && <div className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-green-500/20"><CheckCircle className="w-4 h-4 text-green-400" /><span className="text-green-400 text-sm font-medium">Offer Accepted</span></div>}
        {offer?.status === 'declined' && <div className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-red-500/20"><XCircle className="w-4 h-4 text-red-400" /><span className="text-red-400 text-sm font-medium">Offer Declined</span></div>}
        {offer?.status === 'pending' && isSender && <div className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-[#8B5CF6]/20"><Clock className="w-4 h-4 text-[#A78BFA]" /><span className="text-[#A78BFA] text-sm font-medium">Awaiting Response</span></div>}
        <span className="text-xs mt-2 block text-[#64748B]">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    );
  };

  return (
    <div className="h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white flex flex-col" data-testid="messages-page">
      {/* Offer Modal */}
      <AnimatePresence>
        {showOfferModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowOfferModal(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-[#1E1E3A] rounded-2xl p-6 max-w-md w-full border border-white/10" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6"><div className="flex items-center gap-3"><Gift className="w-6 h-6 text-[#A78BFA]" /><h3 className="font-syne text-xl font-bold text-white">Send Custom Offer</h3></div><button onClick={() => setShowOfferModal(false)} className="text-[#9CA3AF] hover:text-white"><X className="w-5 h-5" /></button></div>
              <div className="space-y-4">
                <div><Label className="text-[#9CA3AF]">Offer Title *</Label><Input value={offerTitle} onChange={(e) => setOfferTitle(e.target.value)} placeholder="e.g., Custom Logo Design" className="bg-white/5 border-white/10 text-white mt-2" data-testid="offer-title" /></div>
                <div><Label className="text-[#9CA3AF]">Description</Label><Textarea value={offerDescription} onChange={(e) => setOfferDescription(e.target.value)} placeholder="Describe what's included..." className="bg-white/5 border-white/10 text-white mt-2 min-h-[80px]" data-testid="offer-description" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-[#9CA3AF]">Price ($) *</Label><Input type="number" value={offerPrice} onChange={(e) => setOfferPrice(e.target.value)} placeholder="0.00" className="bg-white/5 border-white/10 text-white mt-2" data-testid="offer-price" /></div>
                  <div><Label className="text-[#9CA3AF]">Delivery (days) *</Label><Input type="number" value={offerDeliveryDays} onChange={(e) => setOfferDeliveryDays(e.target.value)} placeholder="7" className="bg-white/5 border-white/10 text-white mt-2" data-testid="offer-delivery" /></div>
                </div>
                <Button onClick={sendCustomOffer} disabled={sendingOffer} className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold mt-4" data-testid="send-offer-btn">{sendingOffer ? 'Sending...' : 'Send Custom Offer'}</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="shrink-0 px-5 py-3 border-b border-white/5 flex items-center justify-between bg-[#12122A]/80 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              // Smart back: if a conversation is selected, go to conversation list;
              // otherwise go to the right dashboard for the user's role.
              if (userId) {
                navigate('/messages');
              } else if (isVendor) {
                navigate('/dashboard/vendor');
              } else {
                // Consumer dashboard lives at /dashboard/consumer.
                // The previous '/dashboard' route doesn't exist → black screen.
                navigate('/dashboard/consumer');
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-[#9CA3AF] hover:text-white hover:bg-white/10 transition-all text-xs font-medium"
            data-testid="messages-back-btn"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <ENLogo size="sm" linkTo="" />
          <h1 className="font-syne text-lg font-bold text-white">Messages</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-[10px] font-medium ${wsConnected ? 'text-green-400' : 'text-[#9CA3AF]'}`}>
            {wsConnected ? <Wifi size={10} /> : <WifiOff size={10} />}
            {wsConnected ? 'Live' : 'Reconnecting...'}
          </span>
          {unreadCount > 0 && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8B5CF6]/20 text-[#A78BFA] text-xs font-bold"><Bell size={10} />{unreadCount}</span>}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Conversations List */}
        <aside className={`w-full md:w-80 bg-[#12122A]/60 border-r border-white/5 flex flex-col ${userId ? 'hidden md:flex' : 'flex'}`} data-testid="conversations-list">
          <div className="flex-1 overflow-y-auto">
            {conversations.length > 0 ? conversations.map((conv) => (
              <Link key={conv.other_user_id} to={`/messages/${conv.other_user_id}`} className={`flex items-center gap-3 p-4 border-b border-white/5 hover:bg-white/5 transition-colors ${userId === conv.other_user_id ? 'bg-[#8B5CF6]/10 border-l-2 border-l-[#8B5CF6]' : ''}`} data-testid={`conversation-${conv.other_user_id}`}>
                <div className="w-11 h-11 rounded-full overflow-hidden bg-[#2D2D52] flex-shrink-0">
                  {conv.other_user?.picture ? <img src={conv.other_user.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]"><Users className="w-5 h-5" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium truncate text-sm">{conv.vendor?.business_name || conv.other_user?.name || 'User'}</span>
                    {conv.unread_count > 0 && <span className="w-5 h-5 rounded-full bg-[#8B5CF6] text-white text-[10px] flex items-center justify-center font-bold">{conv.unread_count}</span>}
                  </div>
                  <p className="text-[#64748B] text-xs truncate mt-0.5">{conv.last_message?.message_type === 'offer' ? 'Custom Offer' : conv.last_message?.content || 'No messages'}</p>
                </div>
              </Link>
            )) : (
              <div className="p-8 text-center"><MessageSquare className="w-12 h-12 text-[#2D2D52] mx-auto mb-4" /><p className="text-[#64748B]">No conversations yet</p></div>
            )}
          </div>
        </aside>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!userId ? 'hidden md:flex' : 'flex'}`}>
          {userId ? (
            <>
              <div className="p-4 border-b border-white/5 flex items-center justify-between bg-[#12122A]/40">
                <div className="flex items-center gap-3">
                  <Link to="/messages" className="md:hidden"><Button variant="ghost" size="icon" className="text-[#9CA3AF]"><ArrowLeft className="w-5 h-5" /></Button></Link>
                  <button onClick={() => window.history.back()} className="hidden md:flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 text-[#9CA3AF] hover:text-white transition-all" data-testid="messages-go-back"><ArrowLeft size={16} /></button>
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-[#2D2D52]">
                    {selectedUser?.picture ? <img src={selectedUser.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]"><Users className="w-5 h-5" /></div>}
                  </div>
                  <div>
                    <span className="text-white font-medium text-sm">{selectedUser?.name || 'User'}</span>
                    {typingUser === userId && <p className="text-[#A78BFA] text-xs animate-pulse">typing...</p>}
                  </div>
                </div>
                {isVendor && <Button onClick={() => setShowOfferModal(true)} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold text-sm" data-testid="send-offer-modal-btn"><Gift className="w-4 h-4 mr-2" /> Send Offer</Button>}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-thread">
                {messages.map((msg, index) => (
                  <motion.div key={msg.message_id || index} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}>
                    {msg.message_type === 'offer' ? renderOfferMessage(msg) : msg.is_system ? (
                      <div className="max-w-[85%] p-3 rounded-xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 text-center">
                        <p className="text-[#A78BFA] text-sm">{msg.content}</p>
                        <span className="text-[10px] text-[#64748B] mt-1 block">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    ) : (
                      <div className={`max-w-[70%] p-3 rounded-2xl ${msg.sender_id === user?.user_id ? 'bg-[#8B5CF6] text-white rounded-br-none' : 'bg-[#2D2D52] text-[#E2E8F0] rounded-bl-none'}`}>
                        <p className="text-sm">{msg.content}</p>
                        <span className={`text-[10px] mt-1 block ${msg.sender_id === user?.user_id ? 'text-[#C4B5FD]' : 'text-[#64748B]'}`}>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    )}
                  </motion.div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={sendMessage} className="p-4 border-t border-white/5 bg-[#12122A]/40">
                <div className="flex gap-3">
                  <Input value={newMessage} onChange={(e) => { setNewMessage(e.target.value); handleTyping(); }} placeholder="Type a message..." className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]" data-testid="message-input" />
                  <Button type="submit" className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="send-message"><Send className="w-5 h-5" /></Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center"><MessageSquare className="w-16 h-16 text-[#2D2D52] mx-auto mb-4" /><p className="text-[#64748B]">Select a conversation to start messaging</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
